import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../Reusablecomponents/Pagination";
import ExportButton from "../Reusablecomponents/ExportButton";
import ReusableTable from "../Reusablecomponents/ReusableTable";
import "../css/others/Table.css";
import { apiCall } from "../main/ApiCall";
import axios from "axios";
import { FaUpload, FaSort, FaSortUp, FaSortDown, FaEye, FaEyeSlash, FaColumns } from "react-icons/fa";
import { TbMessageCircle } from "react-icons/tb";
import ToastPopup from "../popups/ToastPopup";
import { buildUserPayload } from "../main/buildUserPayload";
import EncryptionUtils from "../main/encryptionUtils";
import { ToastMessage, createToastMessage } from "../interFaces/interfaces";
const ProjectDetail: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [file, setFile] = useState<File | null>(null);
  const [, setUploadError] = useState<string>("");
  const [fields, setFields] = useState<any>({});
  const [hasValidMappings, setHasValidMappings] = useState<boolean>(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "ascending" | "descending" } | null>(null);
  const [showColumnSelector, setShowColumnSelector] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [tempSelectedColumns, setTempSelectedColumns] = useState<string[]>([]);
  const navigate = useNavigate();
  const clientId = localStorage.getItem("clientId");
  const { roleName } = EncryptionUtils.getAuthInfo();
  const allColumns = useMemo(() => [{ key: "displayProjectId", label: "Project ID", sortable: true, required: true }, { key: "projectName", label: "Project Name", sortable: true, required: true }, { key: "projectCategory", label: "Project Category", sortable: true, required: false }, { key: "accountName", label: "Account Name", sortable: true, required: false }, { key: "businessUnit", label: "Business Unit", sortable: false, required: false }, { key: "projectManagerName", label: "Project Manager Name", sortable: false, required: false }, { key: "deliveryManagerName", label: "Delivery Manager Name", sortable: false, required: false }, { key: "qualityConsultantName", label: "Quality Consultant Name", sortable: false, required: false }, { key: "teamSize", label: "Team Size", sortable: false, required: false }, { key: "projectLocation", label: "Project Location", sortable: false, required: false }, { key: "projectLifeCycle", label: "Project Life Cycle", sortable: false, required: false }, { key: "targetML", label: "Target ML", sortable: false, required: false }, { key: "billingType", label: "Billing Type", sortable: false, required: false }, { key: "projectStartDate", label: "Project Start Date", sortable: false, required: false }, { key: "projectEndDate", label: "Project End Date", sortable: false, required: false }, { key: "projectCreatedDate", label: "Project Created Date", sortable: false, required: false }, { key: "projectUpdatedDate", label: "Project Updated Date", sortable: false, required: false }], []);
  const checkMappings = useCallback(async () => {
    try {
      if (!clientId) return;
      const response = await apiCall("get", "/api/client-mappings", null, { clientId });
      const hasMappings = Object.keys(response?.mappedData || {}).length > 0;
      const hasRawData = Object.keys(response?.rawData || {}).length > 0;
      const isValid = hasMappings && hasRawData;
      setHasValidMappings(isValid);
      if (!isValid) {
        setMessage(createToastMessage("Please complete fields mapping before data upload", "warning", 3000));
      }
    } catch (error) {
      setHasValidMappings(false);
      setMessage(createToastMessage("Please complete fields mapping before data upload", "warning", 3000));
    }
  }, [clientId]);
  useEffect(() => {
    const permissions = EncryptionUtils.get<any>("permissions", null);
    if (permissions?.tabs?.["projects"]) {
      setFields(permissions.tabs["projects"].fields);
      checkMappings();
    } else {
      console.log("No permissions found for 'projects' tab");
    }
  }, [checkMappings]);
  useEffect(() => {
    const loadSavedColumns = () => {
      try {
        const savedColumns = EncryptionUtils.get<string[]>("projectTableColumns", []);
        if (savedColumns && savedColumns.length > 0) {
          const requiredColumnKeys = allColumns.filter(col => col.required).map(col => col.key);
          const mergedColumns = Array.from(new Set([...requiredColumnKeys, ...savedColumns]));
          const validColumns = mergedColumns.filter((key: string) => allColumns.some(col => col.key === key));
          if (validColumns.length > 0) {
            setSelectedColumns(validColumns);
            EncryptionUtils.set("projectTableColumns", validColumns);
            return;
          }
        }
      } catch (error) {
        console.error("Error loading saved columns:", error);
      }
      const allColumnKeys = allColumns.map(col => col.key);
      setSelectedColumns(allColumnKeys);
      EncryptionUtils.set("projectTableColumns", allColumnKeys);
    };
    loadSavedColumns();
  }, [allColumns]);
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        let requestBody: { field: string; operator: string; value: string }[] = [
          { field: "clientId", operator: "=", value: clientId ?? "" },
        ];
        if (roleName !== "Admin") {
          const payload = buildUserPayload();
          if (payload) requestBody.push(payload);
        }
        const response = await apiCall("post", `/api/projects/search?page=${currentPage - 1}&size=${itemsPerPage}`, requestBody);
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [clientId, roleName, currentPage, itemsPerPage]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
      setUploadError("");
    }
  };
  const handleBulkUpload = async () => {
    if (!file) {
      setUploadError("Please choose a file before uploading.");
      return;
    }
    try {
      const presignedUrlResponse = await apiCall("get", "/api/bulk-upload/s3presigneduploadurl", null, { fileName: file.name, action: "bulk_upload" });
      const presignedUrl = presignedUrlResponse;
      await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
      setMessage(createToastMessage("File uploaded successfully!", "success", 3000));
      if (!clientId) {
        setMessage(createToastMessage("Client ID not found in localStorage.", "error", 3000));
        return;
      }
      const urlObj = new URL(presignedUrl);
      const fileKey = decodeURIComponent(urlObj.pathname.substring(1));
      await apiCall("post", "/api/bulk-upload/complete", { clientId, fileKey });
      await apiCall("get", "/api/bulk-upload/process-messages");
      setMessage(createToastMessage("File processed successfully!", "success", 3000));
      setFile(null);
      setTimeout(() => window.location.reload(), 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage || "Error uploading file.", "error", 3000));
    }
  };
  const handleAccountIdClick = (row: any) => { navigate(`/projectdetails/${row.projectId}`) };
  const fetchAllProjects = async () => {
    try {
      let requestBody: { field: string; operator: string; value: string }[] = [
        { field: "clientId", operator: "=", value: clientId ?? "" },
      ];
      if (roleName !== "Admin") {
        const payload = buildUserPayload();
        if (payload) requestBody.push(payload);
      }
      const response = await apiCall("post", `/api/projects/search?page=0&size=${totalElements}&clientId=${clientId}`, requestBody);
      return response.content;
    } catch (error) {
      console.error("Error fetching all projects:", error);
      return [];
    }
  };
  const requestSort = useCallback(
    (key: string) => {
      let direction: "ascending" | "descending" = "ascending";
      if (sortConfig && sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );
  const getSortIcon = useCallback(
    (key: string) => { if (!sortConfig || sortConfig.key !== key) return <FaSort />; return sortConfig.direction === "ascending" ? <FaSortUp /> : <FaSortDown /> },
    [sortConfig]
  );
  const sortedData = useMemo(() => {
    if (!sortConfig) return data;
    return [...data].sort((a, b) => {
      if (sortConfig.key === "projectId") {
        const aValue = parseInt(a.projectId);
        const bValue = parseInt(b.projectId);
        if (aValue < bValue) return sortConfig.direction === "ascending" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "ascending" ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);
  const openColumnSelector = useCallback(() => {
    setTempSelectedColumns(selectedColumns);
    setShowColumnSelector(true);
  }, [selectedColumns]);
  const closeColumnSelector = useCallback(() => {
    setShowColumnSelector(false);
  }, []);
  const applyColumnSelector = useCallback(() => {
    setSelectedColumns(tempSelectedColumns);
    EncryptionUtils.set("projectTableColumns", tempSelectedColumns);
    setShowColumnSelector(false);
  }, [tempSelectedColumns]);
  const handleTempColumnToggle = useCallback(
    (columnKey: string) => {
      const column = allColumns.find(col => col.key === columnKey);
      if (column?.required) return;
      setTempSelectedColumns(prev => {
        if (prev.includes(columnKey)) {
          const requiredColumnKeys = allColumns.filter(c => c.required).map(c => c.key);
          const newColumns = prev.filter(key => key !== columnKey);
          const hasRequired = requiredColumnKeys.every(key => newColumns.includes(key));
          return hasRequired ? newColumns : prev;
        } else {
          return [...prev, columnKey];
        }
      });
    },
    [allColumns]
  );
  const toggleAllTempColumns = useCallback(() => {
    if (tempSelectedColumns.length === allColumns.length) {
      const requiredColumnKeys = allColumns.filter(col => col.required).map(col => col.key);
      setTempSelectedColumns(requiredColumnKeys);
    } else {
      const allColumnKeys = allColumns.map(col => col.key);
      setTempSelectedColumns(allColumnKeys);
    }
  }, [allColumns, tempSelectedColumns.length]);
  const resetTempToDefaultColumns = useCallback(() => {
    const allColumnKeys = allColumns.map(col => col.key);
    setTempSelectedColumns(allColumnKeys);
  }, [allColumns]);
  const columns = useMemo(() =>
    allColumns
      .filter(col => selectedColumns.includes(col.key))
      .map(col => {
        const column: any = { key: col.key, label: col.label, render: col.key === "displayProjectId" ? (value: any) => value : undefined, required: col.required || false };
        if (col.sortable) {
          column.headerRender = (label: string) => (
            <div className="sortable-header" onClick={() => requestSort(col.key === "displayProjectId" ? "projectId" : col.key)}>{label}
              <span className="sort-icon">{getSortIcon(col.key === "displayProjectId" ? "projectId" : col.key)}</span>
            </div>
          );
          column.sortable = true;
        }
        return column;
      }),
    [allColumns, selectedColumns, getSortIcon, requestSort]
  );
  const processedData = useMemo(() =>
    sortedData.map(item => ({
      ...item,
      displayProjectId: (
        <div className="message">{item.projectId}
          {item.isHistoryAvailable && (
            <TbMessageCircle className="messenger-icon" title="Historical messages available" />
          )}
        </div>
      ),
    })),
    [sortedData]
  );
  const handleDailyRefreshClick = () => {
    const fileInput = document.getElementById("file-upload") as HTMLInputElement | null;
    if (fileInput) fileInput.click();
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Projects</h2>
        <div className="table-title-actions">
          <button className="column-selector-button" onClick={openColumnSelector}><FaColumns /> Columns ({selectedColumns.length}/{allColumns.length})</button>
          {fields.upload && (
            <>
              <button type="button" className={`bulk-upload-button ${file || !hasValidMappings ? "disabled" : ""}`} onClick={handleDailyRefreshClick} disabled={!!file || !hasValidMappings}>📁 Daily Refresh</button>
              <input id="file-upload" type="file" accept=".csv, .xlsx, .json" onChange={handleFileChange} className="hidden-input" disabled={!!file} />
            </>
          )}
          {file && (
            <button onClick={handleBulkUpload} className="bulk-upload-button"><FaUpload /> Upload File</button>
          )}
          <ExportButton data={data} columns={columns.map(col => (col.key === "displayProjectId" ? { ...col, key: "projectId" } : col))} fetchAllData={fetchAllProjects} fileName="projects" />
        </div>
      </div>
      {showColumnSelector && (
        <div className="column-selector-modal">
          <div className="column-selector-content">
            <div className="column-selector-header">
              <h3>Select Columns to Display</h3>
              <button className="close-button" onClick={closeColumnSelector}>&times;</button>
            </div>
            <div className="column-selector-info">
              <p className="info-text">
                <strong>Note:</strong><br />Project ID and Project Name are required columns and cannot be hidden.
              </p>
            </div>
            <div className="column-list">
              <div className="buttons">
                <button className="deselect" onClick={toggleAllTempColumns}>{tempSelectedColumns.length === allColumns.length ? "Deselect All" : "Select All"}</button>
                <button className="reset-btn" onClick={resetTempToDefaultColumns}>Reset to Default</button>
              </div>
              {allColumns.map(column => (
                <div key={column.key} className={`column-item ${tempSelectedColumns.includes(column.key) ? "selected" : ""} ${column.required ? "required-column" : ""}`} onClick={() => !column.required && handleTempColumnToggle(column.key)} title={column.required ? "Required column (cannot be hidden)" : ""}>
                  <span className="column-checkbox">
                    {column.required ? (
                      <span className="required-icon">🔒</span>
                    ) : tempSelectedColumns.includes(column.key) ? (
                      <FaEye />
                    ) : (
                      <FaEyeSlash />
                    )}
                  </span>
                  <span className="column-label">  {column.label}  {column.required && <span className="required-badge">Required</span>}</span>
                  {column.sortable && <span className="column-sortable">Sortable</span>}
                </div>
              ))}
            </div>
            <div className="column-selector-footer">
              <p>Selected: {tempSelectedColumns.length} of {allColumns.length} columns</p>
              <div className="footer-buttons">
                <button className="cancelbutton" onClick={closeColumnSelector}>Cancel</button>
                <button className="savebutton" onClick={applyColumnSelector}>Apply</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <>
        {data.length === 0 ? (
          <div className="empty-table-state">
            <ReusableTable columns={columns} data={[]} {...(fields.view ? { clickableColumn: "displayProjectId", onRowClick: handleAccountIdClick } : {})} />
          </div>
        ) : (
          <ReusableTable columns={columns} data={processedData} {...(fields.view ? { clickableColumn: "displayProjectId", onRowClick: handleAccountIdClick } : {})} />
        )}
      </>
      <Pagination currentPage={currentPage} totalPages={totalPages} totalElements={totalElements} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} onItemsPerPageChange={setItemsPerPage} />
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default ProjectDetail;